/**
 * Entity Result Row
 *
 * THE row for every cmdk list in the palette — global search results and
 * move/copy destinations alike. Both used to be near-identical components with
 * a comment on one saying it "intentionally mirrors" the other, which is the
 * arrangement that let them drift: search grew a breadcrumb and the destination
 * picker didn't, so the modal you use to CHOOSE a collection was the one that
 * couldn't tell two same-named collections apart.
 *
 * What varies between the two is expressed as props, not as a second component:
 *
 *   search       inline location beside the title, snippet under it, timestamp trailing
 *   destination  location on its own line, no snippet
 *
 * Geometry is fixed here (px=3 py=3, 32px tile, 12px gutters, sm/xs type) so a
 * change lands on both surfaces at once.
 */

import type { ReactNode } from 'react';
import { Box, HStack, VStack, Text, VisuallyHidden } from '@chakra-ui/react';
import type { TextProps } from '@chakra-ui/react';
import { Command } from 'cmdk';
import type { EntityLocation } from '@bower/shared';
import { EntityIconTile } from '../../ui/EntityIconTile';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { formatEntityLocation } from '../../../utils/entityLocation';

/**
 * `inline` puts the location after the title ("Methods in XR Demo / Assay Dev")
 * — right where the title is the payload and location is a qualifier.
 * `block` gives it its own line, for the destination picker where the location
 * IS the thing being chosen between.
 */
export type LocationPlacement = 'inline' | 'block';

interface EntityResultRowProps {
  /** cmdk's item value — what selection and keyboard navigation key on. */
  value: string;
  onSelect: () => void;
  /** Entity type driving the icon tile. Any string; unknown falls back to note. */
  type: string;
  title: string;
  /** The title as rendered, e.g. with the matched words emphasised. Defaults to `title`. */
  titleContent?: ReactNode;
  /** Ancestor chain from the server. Absent means "no location to show". */
  location?: EntityLocation;
  placement?: LocationPlacement;
  /**
   * Replaces the computed breadcrumb outright. For rows that ARE a root and so
   * have nothing above them to describe — the workspace destination, whose
   * computed location would otherwise repeat its own title.
   */
  locationLabel?: string;
  /** Second line under the title. Search's matched-terms snippet. */
  snippet?: ReactNode;
  /** Right-hand slot. Search's relative timestamp. */
  trailing?: ReactNode;
  /**
   * Entity type spoken to assistive tech, e.g. "Collection".
   *
   * The icon tile does NOT carry this: `BowerIcon` renders `aria-hidden`, and
   * `Command.Item` is `role="option"`, whose accessible name comes from its
   * content — so an icon-only type cue reaches sighted users and nobody else.
   */
  typeLabel?: string;
}

/**
 * A breadcrumb that clips its HEAD and never its LEAF.
 *
 * Two boxes, not one string, with the head shrinking a hundred times more
 * readily than the leaf. A single clamped line does the opposite — it keeps the
 * start and drops the end, which on a path throws away the immediate parent,
 * i.e. exactly the segment that separates two collections with the same name.
 *
 * The leaf is PRIORITISED, not exempt. It used to be `flexShrink={0}`, which
 * made the row wider than its container whenever the leaf alone overran, and
 * flex overflow lands on whatever is next to it — here the timestamp, which it
 * painted straight over. A weighted shrink keeps the same reading order (the
 * head disappears first, and is all but gone before the leaf loses a character)
 * while leaving the row able to honour its own width.
 */
function LocationText({
  head,
  leaf,
  prefix,
  tooltip,
  ...rest
}: {
  head: string;
  leaf: string;
  prefix?: string;
  tooltip?: string;
} & Omit<TextProps, 'title'>) {
  const fullHead = `${prefix ?? ''}${head}`;
  const headText = fullHead.trimEnd();
  const headTail = fullHead.slice(headText.length);
  return (
    <HStack as="span" spacing={0} minW={0} overflow="hidden" title={tooltip} {...rest}>
      {(prefix || head) && (
        <Text
          as="span"
          fontSize="xs"
          color="text.muted"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          minW={0}
          flexShrink={100}
        >
          {headText}
          {/* The trailing space of "in " or of the " / " separator collapses at
              the edge of this box ("inXR Demo", "XR Demo /Workspace"). Only that
              space is kept with `pre`; a name's own spacing still collapses, and
              the text read aloud or copied keeps its space. */}
          {headTail && (
            <Text as="span" whiteSpace="pre">
              {headTail}
            </Text>
          )}
        </Text>
      )}
      {/* Shrinks only AFTER the head has given up everything it has: 1 against
          the head's 100, so a hundred pixels come out of the head for every one
          out of the leaf. It is not flexShrink={0}, which is what let a long
          leaf push the whole row past its box and paint over the timestamp —
          a refusal to shrink is a promise the layout cannot keep, and the
          overflow lands on a neighbour rather than on the text that made it. */}
      <Text
        as="span"
        fontSize="xs"
        color="text.muted"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        minW={0}
        flexShrink={1}
      >
        {leaf}
      </Text>
    </HStack>
  );
}

export function EntityResultRow({
  value,
  onSelect,
  type,
  title,
  titleContent,
  location,
  placement = 'inline',
  locationLabel,
  snippet,
  trailing,
  typeLabel,
}: EntityResultRowProps) {
  const { workspace } = useWorkspace();
  const formatted = formatEntityLocation(location, workspace?.name);
  // An override is a whole label with nothing above it, so it is all leaf.
  const label = locationLabel ? { head: '', leaf: locationLabel } : formatted;
  // Kept even when nothing was elided: CSS still clips a path the formatter
  // left whole, so the tooltip is the only place the full chain is readable.
  const tooltip = locationLabel ? undefined : formatted?.full;

  const inline = placement === 'inline' ? label : undefined;
  const block = placement === 'block' ? label : undefined;

  // Two-line rows centre badly against a 32px tile; top-align them so the tile
  // sits with the title rather than floating between the two lines.
  const isStacked = Boolean(snippet || block);

  return (
    <Command.Item value={value} onSelect={onSelect} style={{ cursor: 'pointer' }}>
      <HStack px={3} py={3} spacing={3} w="100%" align={isStacked ? 'flex-start' : 'center'}>
        <EntityIconTile type={type} size="lg" />
        {typeLabel && <VisuallyHidden>{typeLabel}</VisuallyHidden>}

        <VStack flex={1} minW={0} spacing={0} align="stretch">
          <HStack spacing={0} minW={0}>
            {/* Semibold: on a row where the breadcrumb beside it is the same
                size, weight is the only thing that says which half is the
                answer and which is the context. */}
            <Text
              fontSize="sm"
              fontWeight="semibold"
              color="text.primary"
              noOfLines={1}
              flexShrink={1}
              minW={0}
            >
              {titleContent ?? title}
            </Text>
            {inline && (
              <LocationText
                head={inline.head}
                leaf={inline.leaf}
                prefix="in "
                tooltip={tooltip}
                ml={1.5}
                // Shrinks far more readily than the title beside it. On a search
                // row the title is the payload, so the location gives up space
                // first; before this it was flexShrink={0} and a full path
                // squeezed the title to 55px at a 420px viewport.
                flexShrink={100}
              />
            )}
          </HStack>

          {block && (
            <LocationText
              head={block.head}
              leaf={block.leaf}
              tooltip={tooltip}
              lineHeight="short"
            />
          )}

          {snippet && (
            <Text fontSize="xs" color="text.muted" noOfLines={1} lineHeight="short">
              {snippet}
            </Text>
          )}
        </VStack>

        {/* The timestamp is never the thing that gives way. Unwrapped it was
            a plain flex item at the default shrink, so a long breadcrumb beside
            it squeezed it to nothing and then overlapped what was left. */}
        {trailing && (
          <Box flexShrink={0} display="flex" alignItems="center">
            {trailing}
          </Box>
        )}
      </HStack>
    </Command.Item>
  );
}
